/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Typography, Paper, Drawer } from '@material-ui/core';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import { useLocation, navigate } from '@gatsbyjs/reach-router';
import { useReactiveVar } from '@apollo/client';
import { useGlobal } from '@rcg/ui-react';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import useLdFlags from '../../hooks/useLdFlags';
import useRestrictedBookingGuard from '../../hooks/useRestrictedBookingGuard';
import Button from '../../components/UiComponents/Button';
import { trackView } from '../../utils/analytics/bookFlightsTracking';
import AirLoader from '../../components/AirLoader';
import {
  getCookies,
  getUniqueConfirmationNumbers,
  getManageRedirectionUrl,
  setSessionTokenFromUrl,
} from '../../utils/common';
import ItineraryDetails from '../../components/ItineraryDetails';
import FlighInfoCollapsable from '../../components/FlightInfoCollapsable';
import CancellationFees from '../../components/CancellationFees';
import TravelPlansUnified from '../../components/TravelPlansUnified';
import { setSessionStorageItem, storeBookingToken } from '../../operations/sessionStorage/utils';
import { setBookingProfile } from '../../operations/localState/setBookingProfile';
import { bookingProfileVar, isLoggedInVar } from '../../operations/localState/reactiveVariables';
import setSearchResults from '../../operations/localState/setSearchResults';

import setParams from '../../operations/localState/setParams';

import useBookingRetrieve from '../../operations/dataAccess/useBookingRetrieve';
import useFlightProfile from '../../operations/dataAccess/useFlightProfile';
import {
  SESSION_STORAGE_GDS_LOCATOR,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  NAVIGATION_PATHS,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
  TRAVEL_AGENT,
  GROUP_BOOKING,
  COOKIE_KEYS,
  SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
} from '../../utils/constants';
import MissingFlights from '../../components/MissingFlights';
import PaymentDetails from '../../components/PaymentDetails';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import useStyles from './index.style';

const Manage = () => {
  const classes = useStyles();
  const { ldFlagsLoading, ldFlags } = useLdFlags();

  const getDictionary = useDictionary(Dictionary);
  const {
    data: { lang, currency, country: globalCountry, market },
  } = useGlobal();
  const baseUrlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const bookingProfile = useReactiveVar(bookingProfileVar);
  const [profileName, setProfileName] = useState(null);
  setParams(null);
  setSearchResults({
    flightFilters: null,
    matchingFlights: [],
  });
  setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
  setSessionStorageItem(SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT, null);
  setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
  setSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY, '');
  setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, null);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const [openCancellationFeesDrawer, setOpenCancellationFeesDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFlightInfo, setSelectedFlightInfo] = useState({});
  const container = window !== undefined ? () => window.document.body : undefined;
  const [executeFlightProfile] = useFlightProfile({
    fetchPolicy: 'network-only',
    variables: {
      limit: -1,
      ignoreCache: true,
    },
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search);
  const bookingId = searchParams.get('bookingId');
  const shipCode = searchParams.get('shipCode');
  const sailDate = searchParams.get('date');
  const lastName = searchParams.get('lastName');

  const [executeRetrieveBooking] = useBookingRetrieve({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const isRestrictedBooking = useRestrictedBookingGuard(bookingProfile?.currentBooking, ldFlags);

  const supportPhoneNumber = ldFlags?.[SUPPORT_PHONE_NUMBER_LD_FLAG];
  const [trackViewSent, setTrackViewSent] = useState(false);

  const loadBooking = async () => {
    if (bookingId && shipCode && sailDate && lastName) {
      const { data } = await executeRetrieveBooking({
        variables: {
          bookingId,
          lastName,
          sailDate,
          shipCode,
        },
      });

      if (!data?.retrieveBooking?.token) {
        navigate(`${baseUrlPath}${NAVIGATION_PATHS.bookFlights}`);
        return;
      }
      const { token } = data.retrieveBooking;
      storeBookingToken(token);
      isLoggedInVar(true);
    }
  };

  const getFlightsBooked = (flightProfile) => {
    return flightProfile?.guests?.some(({ flightGuest }) => flightGuest?.guestFlights);
  };

  function groupGuestsByGdsLocator(guests) {
    if (!guests) {
      return {};
    }
    const groupedGuests = {};
    for (const guest of guests) {
      const gdsLocators = guest?.flightGuest?.gdsLocators || [];

      const sortedGdsLocators = [...gdsLocators].sort((a, b) => a.localeCompare(b));
      const groupKey = JSON.stringify(sortedGdsLocators);

      if (!groupedGuests[groupKey]) {
        groupedGuests[groupKey] = [];
      }

      groupedGuests[groupKey].push(guest);
    }
    return groupedGuests;
  }

  const loadFlightProfile = async () => {
    setSessionTokenFromUrl();
    const { data } = await executeFlightProfile();
    // No booking in context (e.g. landing here without a session): GraphQL surfaced this as a
    // query error so the page never rendered, but the REST profile answers 200 with null
    // sections — redirect to Book Flights instead of rendering an undefined page.
    if (data && !data.flightProfile && !data.currentBooking) {
      navigate(`${baseUrlPath}${NAVIGATION_PATHS.bookFlights}`);
      return null;
    }
    if (data) {
      setBookingProfile(data);
      const { flightProfile } = data || {};
      const hasSomeGuestsFlights = getFlightsBooked(flightProfile);
      if (hasSomeGuestsFlights)
        navigate(`${baseUrlPath}${NAVIGATION_PATHS.manage}`, { replace: true });
    }
    return data;
  };

  const collectTrackingInformation = (groupedGuestsByGdsLocator) => {
    let flightsCountByGds = 0;
    const flightsCosts = [];
    const flightStops = [];
    const confirmationNumbers = [];

    // Loop through each GDS locator group
    for (const gdsLocator of Object.keys(groupedGuestsByGdsLocator) || []) {
      const flightByLocator = groupedGuestsByGdsLocator[gdsLocator][0];

      // Check if the group has flights
      if (flightByLocator?.flightGuest?.gdsLocators?.length > 0) {
        flightsCountByGds += 1;

        flightsCosts.push(`${flightByLocator.flightGuest?.airChargesAmount}`);

        // Extract unique confirmation numbers
        confirmationNumbers.push(
          ...getUniqueConfirmationNumbers(flightByLocator.flightGuest?.guestFlights),
        );

        // Extract and format airport codes for flight stops
        const airportCodes = flightByLocator.flightGuest?.guestFlights?.flatMap((flight) =>
          flight.flightSegments.flatMap((segment) => [
            segment.departure.airport.code,
            segment.arrival.airport.code,
          ]),
        );

        const formattedStops = airportCodes?.join('-');
        if (formattedStops) {
          flightStops.push(formattedStops);
        }
      }
    }
    return {
      flightsCountByGds,
      flightsCosts,
      confirmationNumbers,
      flightStops,
    };
  };

  const { currentBooking, flightProfile } = bookingProfile || {};

  const excludedBookingTypes = [TRAVEL_AGENT, GROUP_BOOKING];
  const showPaymentInfo = !excludedBookingTypes.includes(currentBooking?.type);

  const groupedGuestsByGdsLocator = groupGuestsByGdsLocator(flightProfile?.guests);
  function getGuestNames(guests) {
    return guests.map((guest) => ({
      firstName: guest.flightGuest.firstName,
      lastName: guest.flightGuest.lastName,
    }));
  }

  const goToBookFlights = () => {
    setSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR, null);
    setSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS, false);
    setSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY, window.location.href);
    navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
  };

  function checkForFlights(guests) {
    // Check if any guest has no gdsLocator means no flights booked
    const isSomeGuestMissingFlight = guests?.some(
      (guest) => !guest.flightGuest.gdsLocators || guest.flightGuest.gdsLocators.length === 0,
    );
    const thereAreNoFlights = guests?.every(
      (guest) => !guest.flightGuest.gdsLocators || guest.flightGuest.gdsLocators.length === 0,
    );
    return {
      isSomeGuestMissingFlight,
      thereAreNoFlights,
    };
  }

  const { isSomeGuestMissingFlight, thereAreNoFlights } = checkForFlights(flightProfile?.guests);
  const renderFlightSummary = Object.keys(groupedGuestsByGdsLocator).map((gdsLocator) => {
    const guestsWithNames = getGuestNames(groupedGuestsByGdsLocator[gdsLocator]);

    const flightByLocator = groupedGuestsByGdsLocator[gdsLocator][0];

    const itHasFlights = flightByLocator?.flightGuest?.gdsLocators?.length > 0;

    if (itHasFlights) {
      return (
        <Paper key={gdsLocator}>
          <FlighInfoCollapsable
            flightInfo={flightByLocator} // fist position since every guest in the group will share same flight information
            guestNames={guestsWithNames}
            supportPhoneNumber={supportPhoneNumber}
            setOpenCancellationFeesDrawer={setOpenCancellationFeesDrawer}
            setSelectedFlightInfo={setSelectedFlightInfo}
          />
        </Paper>
      );
    }
    return null;
  });

  const initialTrackView = (bookingProfileQuery) => {
    // create dataLayer to send to Tealium
    if (bookingProfileQuery) {
      const { sailing } = bookingProfileQuery?.currentBooking || {};
      const groupByGdsLocator = groupGuestsByGdsLocator(bookingProfileQuery?.flightProfile?.guests);

      const { flightsCountByGds, flightsCosts, confirmationNumbers, flightStops } =
        collectTrackingInformation(groupByGdsLocator);

      const flightsData = {
        flightprodCategory: ['Flight'],
        flightprodQuantity: [`${flightsCountByGds}`],
        flightProdCost: flightsCosts,
        flightprodSku: flightStops,
        flightReservationNumber: confirmationNumbers,
        siteSection: 'manageFlight',
      };

      const manageData = {
        pageName: 'airbooking:manageFlight',
        siteSection: flightStops.length > 0 ? 'manageFlight' : 'airbooking',
        pageType: 'manageFlight',
        loginStatus: profileName ? 'Logged In' : 'Anonymous',
        sailDate: sailing?.sailDate,
        ship: sailing?.itinerary?.ship.code,
        bookingID: bookingProfileQuery?.flightProfile?.id,
        itineraryCode: sailing?.itinerary?.code,
        country: globalCountry,
        language: lang,
        currency,
      };

      const dataLayer = {
        ...manageData,
        ...flightsData,
      };

      if (!trackViewSent) {
        const reviewflights = checkForFlights(bookingProfileQuery?.flightProfile?.guests);
        trackView(reviewflights.thereAreNoFlights ? manageData : dataLayer);
        setTrackViewSent(true);
      }
    }
  };

  const handleReturnToAmend = () => {
    window.location.href = getManageRedirectionUrl();
  };

  const handleDrawerToggle = () => {
    setOpenCancellationFeesDrawer(!openCancellationFeesDrawer);
  };

  useEffect(() => {
    const initialLoads = async () => {
      setLoading(true);
      const loyaltyData = getCookies(COOKIE_KEYS.LOYALTY_DATA);
      setProfileName(loyaltyData);
      await loadBooking();
      const profile = await loadFlightProfile();
      initialTrackView(profile);
      setLoading(false);
      if (!bookingProfileVar()) {
        navigate(`${baseUrlPath}${NAVIGATION_PATHS.bookFlights}`);
      }
    };
    initialLoads();
  }, []);

  if (isRestrictedBooking) {
    return <AirLoader open status={getDictionary(Dictionary.loaderRetrieveFlights.key)} />;
  }

  if (bookingProfile && !loading) {
    return (
      <>
        <Helmet>
          <title>{getDictionary(Dictionary.airManageFlightSeoTitle.key)}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <ItineraryDetails />
        <Box className={classes.manageContainer}>
          <AirLoader
            open={ldFlagsLoading}
            status={ldFlagsLoading && getDictionary(Dictionary.loaderRetrieveFlights.key)}
          />
          <div className={classes.backToAmendButtonContainer}>
            <Button
              className="back-amend-button"
              size="small"
              color="primary"
              onClick={handleReturnToAmend}>
              <NavigateBeforeIcon />
              {getDictionary(Dictionary.airManageCruiseBackButtonLabel.key)}
            </Button>
          </div>
          <Typography variant="h1">{getDictionary(Dictionary.airManageTitle.key)}</Typography>

          {flightProfile?.id && (
            <>
              <div className="flight-summary-wrapper">{renderFlightSummary}</div>
              {isSomeGuestMissingFlight && !thereAreNoFlights && <MissingFlights />}
              {thereAreNoFlights && (
                <Paper>
                  <div className="no-flights-content">
                    {getDictionary(Dictionary.airManageNoBookingsLabel.key)}
                  </div>
                  <div className="no-flights-actions">
                    <Button variant="contained" color="primary" onClick={goToBookFlights}>
                      {getDictionary(Dictionary.airManageBookFlightsCTA.key)}
                    </Button>
                  </div>
                </Paper>
              )}
            </>
          )}
          {showPaymentInfo && (
            <>
              <Typography variant="subtitle1">
                {getDictionary(Dictionary.airManagePaymentDetailsTitle.key)}
              </Typography>
              <PaymentDetails booking={currentBooking} />
            </>
          )}
          <TravelPlansUnified supportPhoneNumber={supportPhoneNumber} ldFlags={ldFlags} />
        </Box>
        <Drawer
          container={container}
          variant="temporary"
          anchor="right"
          open={openCancellationFeesDrawer}
          classes={{
            paper: classes.cancellationFeesDrawer,
          }}
          onClose={handleDrawerToggle}>
          <CancellationFees
            setOpenCancellationFeesDrawer={setOpenCancellationFeesDrawer}
            flightInfo={selectedFlightInfo}
            currency={flightProfile?.currency}
          />
        </Drawer>
      </>
    );
  }
  return (
    <AirLoader
      open={loading || ldFlagsLoading}
      status={(loading || ldFlagsLoading) && getDictionary(Dictionary.loaderRetrieveFlights.key)}
    />
  );
};

export default Manage;
